import { yupResolver } from "@hookform/resolvers/yup";
import {
  Flex,
  Text,
  Link,
  Modal,
  type CountryCode,
  CheckboxField,
  scrollToInvalidField,
  FormLayout,
} from "@wayflyer/flyui";
import {
  EditableAddressSearchField,
  useErrorModal,
  type AddressFields,
  type ParsedAddress,
  type PlaceDetails,
} from "shared-ui";
import { RhfDatePickerField, RhfTextField } from "@wayflyer/flyui-hook-form";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { useCustomerAppConfig } from "undercarriage";
import * as Yup from "yup";
import type { FundingApplicationSchemas } from "api-types";

import {
  useDownloadConsentDocument,
  useCreditScoreConsent,
  useCreditScoreConsentMutation,
  useUpdateCreditScoreConsentMutation,
} from "../../queries/credit-score-consent/queries";

import { MESSAGES } from "./message-definitions";

const formatDateWithZeroPadding = (dateString?: string): string | undefined => {
  if (!dateString) {
    return dateString;
  }

  const dateParts = dateString.split("-");
  if (dateParts.length === 3) {
    const [year, month, day] = dateParts;
    const paddedMonth = month.padStart(2, "0");
    const paddedDay = day.padStart(2, "0");
    return `${year}-${paddedMonth}-${paddedDay}`;
  }

  return dateString;
};

export interface CreditCheckConsentFormFields extends AddressFields {
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  ssn?: string;
  explicitNoSsn: boolean;
  maskedSsn?: string | null;
}

type CreditCheckConsentEntryFormProps = {
  formId: string;
  initialValues: CreditCheckConsentFormFields;
  onSubmit: (formData: CreditCheckConsentFormFields) => void;
  onDownloadConsentDocument?: () => void;
};

const CreditCheckConsentEntryForm = ({
  formId,
  initialValues,
  onSubmit,
  onDownloadConsentDocument,
}: CreditCheckConsentEntryFormProps) => {
  const intl = useIntl();
  const [showNoSsnConfirmationModal, setShowNoSsnConfirmationModal] = useState(false);

  const requiredIfHasSsn = (buildRequiredSchema: () => Yup.StringSchema) => {
    return Yup.string().when("explicitNoSsn", {
      is: false,
      then: () => buildRequiredSchema(),
      otherwise: (schema) => schema.optional(),
    });
  };

  const schema = Yup.object().shape({
    address_search: Yup.string().optional(),
    address_line_1: requiredIfHasSsn(() => Yup.string().required()),
    address_line_2: Yup.string().optional(),
    city: requiredIfHasSsn(() => Yup.string().required()),
    state: requiredIfHasSsn(() => Yup.string().required()),
    zip_code: requiredIfHasSsn(() => Yup.string().required()),
    country_code: requiredIfHasSsn(() => Yup.string().required()),
    firstName: requiredIfHasSsn(() => Yup.string().trim().required(intl.formatMessage(MESSAGES.firstNameError))),
    lastName: requiredIfHasSsn(() => Yup.string().trim().required(intl.formatMessage(MESSAGES.lastNameError))),
    dateOfBirth: requiredIfHasSsn(() =>
      Yup.string()
        .trim()
        .required(intl.formatMessage(MESSAGES.dateOfBirthError))
        .test("is-valid-date", intl.formatMessage(MESSAGES.dateOfBirthInvalidError), (value) => {
          if (!value) {
            return false;
          }
          const dateRegex = /^\d{4}-(0?[1-9]|1[0-2])-(0?[1-9]|[12]\d|3[01])$/;
          return dateRegex.test(value);
        })
    ),
    ssn: requiredIfHasSsn(() =>
      Yup.string()
        .required(intl.formatMessage(MESSAGES.ssnError))
        .test("ssn-format", intl.formatMessage(MESSAGES.ssnFormatError), (value) => {
          if (!value) {
            return false;
          }
          const digitsOnly = value.replace(/-/g, "");
          return /^\d{9}$/.test(digitsOnly);
        })
    ),
    explicitNoSsn: Yup.bool(),
  });

  const form = useForm<CreditCheckConsentFormFields>({
    resolver: yupResolver(schema),
    defaultValues: initialValues,
  });

  // eslint-disable-next-line react-hooks/incompatible-library -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
  const explicitNoSsn = form.watch("explicitNoSsn");
  const countryCode = form.watch("country_code") as CountryCode;

  const handleAddressSelect = (parsedAddress: ParsedAddress, _details: PlaceDetails, mainText?: string) => {
    const { city, postalCode, state, streetName, streetNumber } = parsedAddress;
    let addressLine1 = [streetNumber, streetName].filter(Boolean).join(" ") || "";
    if (!addressLine1 && mainText && mainText.trim()) {
      addressLine1 = mainText.trim();
    }

    form.setValue("address_line_1", addressLine1, { shouldDirty: true, shouldValidate: true });
    form.setValue("address_line_2", "", { shouldDirty: true });
    form.setValue("city", city ?? "", { shouldDirty: true, shouldValidate: true });
    form.setValue("state", state ?? "", { shouldDirty: true, shouldValidate: true });
    form.setValue("zip_code", postalCode ?? "", { shouldDirty: true, shouldValidate: true });
  };

  const onConsentSubmit = (formData: CreditCheckConsentFormFields) => {
    const formattedFormData = {
      ...formData,
      dateOfBirth: formatDateWithZeroPadding(formData.dateOfBirth),
    };
    onSubmit(formattedFormData);
  };

  const onSubmitHandler = (formData: CreditCheckConsentFormFields) => {
    if (formData.explicitNoSsn) {
      setShowNoSsnConfirmationModal(true);
    } else {
      onConsentSubmit(formData);
    }
  };

  const handleAddSsn = () => {
    setShowNoSsnConfirmationModal(false);
    form.setValue("explicitNoSsn", false);
  };

  const handleContinueWithoutSsn = () => {
    setShowNoSsnConfirmationModal(false);
    onConsentSubmit(form.getValues());
  };

  return (
    <>
      <form id={formId} onSubmit={form.handleSubmit(onSubmitHandler, scrollToInvalidField)}>
        <Flex direction="column" maxWidth="550px">
          <FormLayout>
            <Flex direction="column" gap="3">
              <RhfTextField
                form={form}
                name="ssn"
                label={intl.formatMessage(MESSAGES.ssnLabel)}
                disabled={explicitNoSsn}
                defaultValue={initialValues.ssn}
                helperText={
                  initialValues.maskedSsn && !form.formState.dirtyFields.ssn && !explicitNoSsn
                    ? { text: intl.formatMessage(MESSAGES.ssnHelperText), showInfoIcon: true }
                    : undefined
                }
                mask={{
                  mask: "999-99-9999",
                  alwaysShowMask: true,
                }}
              />
              <Controller
                name="explicitNoSsn"
                control={form.control}
                render={({ field }) => (
                  <CheckboxField
                    name="explicitNoSsn"
                    options={{
                      name: "explicitNoSsn",
                      label: intl.formatMessage(MESSAGES.noSsnLabel),
                      defaultChecked: initialValues.explicitNoSsn,
                      checked: field.value,
                      onChange: (checked: boolean) => {
                        if (checked) {
                          form.resetField("ssn");
                          form.clearErrors();
                        }
                        field.onChange(checked);
                      },
                    }}
                  />
                )}
              />
            </Flex>

            <Flex direction="row" gap="6">
              <RhfTextField
                form={form}
                name="firstName"
                disabled={explicitNoSsn}
                label={intl.formatMessage(MESSAGES.firstNameLabel)}
              />
              <RhfTextField
                form={form}
                name="lastName"
                disabled={explicitNoSsn}
                label={intl.formatMessage(MESSAGES.lastNameLabel)}
              />
            </Flex>

            <RhfDatePickerField
              form={form}
              name="dateOfBirth"
              disabled={explicitNoSsn}
              label={intl.formatMessage(MESSAGES.dateOfBirthLabel)}
            />

            <EditableAddressSearchField
              form={form}
              disabled={explicitNoSsn}
              fieldName="address_search"
              handleAddressSelect={handleAddressSelect}
              countries={[countryCode]}
              label={intl.formatMessage(MESSAGES.addressLabel)}
              lockFields={{ country_code: countryCode }}
            />

            <Text color="placeholder" size="xs">
              {intl.formatMessage(MESSAGES.agreeAndContinueText, {
                link: (linkText: React.ReactNode) => <Link onClick={onDownloadConsentDocument}>{linkText}</Link>,
              })}
            </Text>
          </FormLayout>
        </Flex>
      </form>

      <Modal
        isOpen={showNoSsnConfirmationModal}
        onClose={handleAddSsn}
        title={intl.formatMessage(MESSAGES.noSsnModalTitle)}
        primaryAction={{
          content: intl.formatMessage(MESSAGES.submitWithoutSsnButton),
          onAction: handleContinueWithoutSsn,
        }}
        tertiaryAction={{
          content: intl.formatMessage(MESSAGES.addSsnButton),
          onAction: handleAddSsn,
        }}
      >
        <Flex direction="column" gap="4">
          <Text>{intl.formatMessage(MESSAGES.noSsnModalContent)}</Text>
        </Flex>
      </Modal>
    </>
  );
};

export interface CreditCheckConsentFormConnectorProps {
  formId: string;
  onContinue: () => void;
}

export const CreditCheckConsent = ({ formId, onContinue }: CreditCheckConsentFormConnectorProps) => {
  const { openErrorModal } = useErrorModal();
  const {
    data: { customerDomain, countryOfIncorporation },
  } = useCustomerAppConfig();
  const { data: existingConsent } = useCreditScoreConsent(customerDomain);
  const createConsentMutation = useCreditScoreConsentMutation(customerDomain);
  const updateConsentMutation = useUpdateCreditScoreConsentMutation(customerDomain);
  const { refetch: fetchConsentDocument } = useDownloadConsentDocument(customerDomain);

  const downloadConsentDocument = async () => {
    try {
      const response = await fetchConsentDocument();
      if (response.data) {
        const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = "Wayflyer Credit Check Consent.pdf";
        a.click();
        window.URL.revokeObjectURL(blobUrl);
      }
    } catch (error) {
      openErrorModal({ variant: "submit-error", error: error as Error });
    }
  };

  const onConsentSubmit = async (formData: CreditCheckConsentFormFields) => {
    try {
      let requestData: FundingApplicationSchemas.CreditScoreConsentRequestSchema = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        dateOfBirth: formData.dateOfBirth,
        ssn: formData.ssn,
        explicitNoSsn: false,
        address: {
          address1: formData.address_line_1,
          address2: formData.address_line_2,
          city: formData.city,
          postcode: formData.zip_code,
          state: formData.state,
          country: formData.country_code,
        },
        consentGranted: true,
      };
      if (formData.explicitNoSsn) {
        requestData = {
          explicitNoSsn: true,
          consentGranted: false,
        };
      }

      if (existingConsent) {
        await updateConsentMutation.mutateAsync(requestData);
      } else {
        await createConsentMutation.mutateAsync(requestData);
      }
      onContinue();
    } catch (error) {
      openErrorModal({ variant: "submit-error", error: error as Error });
    }
  };

  const addressSearch = [
    existingConsent?.address?.address1,
    existingConsent?.address?.address2,
    existingConsent?.address?.city,
    existingConsent?.address?.state,
    existingConsent?.address?.postcode,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <CreditCheckConsentEntryForm
      formId={formId}
      onSubmit={onConsentSubmit}
      onDownloadConsentDocument={downloadConsentDocument}
      initialValues={{
        firstName: existingConsent?.firstName || "",
        lastName: existingConsent?.lastName || "",
        dateOfBirth: existingConsent?.dateOfBirth || undefined,
        ssn: "",
        maskedSsn: existingConsent?.maskedSsn,
        explicitNoSsn: existingConsent?.explicitNoSsn ?? false,
        address_search: addressSearch || "",
        address_line_1: existingConsent?.address?.address1 || "",
        address_line_2: existingConsent?.address?.address2 || "",
        city: existingConsent?.address?.city || "",
        state: existingConsent?.address?.state || "",
        zip_code: existingConsent?.address?.postcode || "",
        country_code: (existingConsent?.address?.country || countryOfIncorporation) as string,
      }}
    />
  );
};
